import type {
  ParameterAdditionOptions,
  ParameterAdditionResult,
} from '../../../shared/lib/hooks/useParameterAddition';
import { invalidateFamilySnapshotCache } from '../../../shared/lib/revit/familySnapshotCache';
import type { BoltConfigurationResult } from '../../../shared/services/boltConfigurationService';
import type { HeadlessQuickFillAutoFillRepairResult } from '../../quick-fill';
import type { MappingParameterRepairKind } from './mappingProjectReadiness';
import { toTraceKey, warnMappingFlow } from './mappingTrace';
import {
  getRepairKindLabel,
  type MappingRepairProgressStepId,
} from './mappingRepairProgress';

export interface RepairActions {
  addParameters: (options?: ParameterAdditionOptions) => Promise<ParameterAdditionResult>;
  addAdditionalCategoryParameters: (options?: ParameterAdditionOptions) => Promise<ParameterAdditionResult>;
  addProcessAndProjectParameters: (options?: ParameterAdditionOptions) => Promise<ParameterAdditionResult>;
  addMappedParameters: (
    parameterNames: string[],
    options?: ParameterAdditionOptions
  ) => Promise<ParameterAdditionResult>;
  addBoltParameters: () => Promise<ParameterAdditionResult>;
  runQuickFillAutoFillRepair: (
    scope: 'classification' | 'full'
  ) => Promise<HeadlessQuickFillAutoFillRepairResult>;
  resolveFamilyRepairKindsAfterClassification?: () => Promise<MappingParameterRepairKind[]>;
}

export interface RepairTraceContext {
  projectKey: string | null;
  signature: string;
}

export interface MappingRepairActionResult {
  kind: MappingParameterRepairKind;
  result: ParameterAdditionResult;
}

export interface MappingRepairRunResult {
  results: MappingRepairActionResult[];
  quickFillResult: HeadlessQuickFillAutoFillRepairResult | null;
  stoppedEarly: boolean;
}

export interface MappingRepairProgressCallbacks {
  onStepStart?: (stepId: MappingRepairProgressStepId) => void;
  onStepComplete?: (stepId: MappingRepairProgressStepId) => void;
  onStepError?: (stepId: MappingRepairProgressStepId) => void;
}

export function hasAppliedRepairWork(result: ParameterAdditionResult): boolean {
  return (
    (result.addedCount || 0) +
    (result.updatedCount || 0) +
    (result.existingCount || 0) +
    (result.modifiedFamilyCount || 0)
  ) > 0;
}

export async function runRepairActions(
  repairKinds: MappingParameterRepairKind[],
  actions: RepairActions,
  traceContext: RepairTraceContext,
  progressCallbacks: MappingRepairProgressCallbacks = {},
  mappedParameterNames: string[] = []
): Promise<MappingRepairRunResult> {
  const results: MappingRepairActionResult[] = [];
  const effectiveRepairKinds = new Set(repairKinds);
  let latestValidatedFamilyRepairKinds = new Set<MappingParameterRepairKind>();
  let ranFamilyMutationAction = false;
  let quickFillResult: HeadlessQuickFillAutoFillRepairResult | null = null;
  const finish = (stoppedEarly = false): MappingRepairRunResult => ({
    results,
    quickFillResult,
    stoppedEarly,
  });
  const options: ParameterAdditionOptions = { suppressNotifications: true };
  const runRepairAction = async (
    kind: MappingParameterRepairKind,
    action: (options?: ParameterAdditionOptions) => Promise<ParameterAdditionResult>,
    progressStepId: MappingRepairProgressStepId = kind
  ): Promise<boolean> => {
    const startedAt = Date.now();
    warnMappingFlow('mapping readiness repair action start', {
      projectKey: traceContext.projectKey,
      signatureKey: toTraceKey(traceContext.signature),
      kind,
    });
    progressCallbacks.onStepStart?.(progressStepId);
    let result: ParameterAdditionResult;
    try {
      result = await action(options);
    } catch (error) {
      progressCallbacks.onStepError?.(progressStepId);
      warnMappingFlow('mapping readiness repair action failed', {
        projectKey: traceContext.projectKey,
        signatureKey: toTraceKey(traceContext.signature),
        kind,
        elapsedMs: Date.now() - startedAt,
        errorType: error instanceof Error ? error.name : typeof error,
        message: error instanceof Error ? error.message : 'Repair action failed.',
      });
      throw error;
    }

    results.push({ kind, result });
    const clean = result.success &&
      (result.failedCount || 0) === 0 &&
      (result.conflictingCount || 0) === 0;
    const totallyFailed = !clean && !hasAppliedRepairWork(result);
    warnMappingFlow('mapping readiness repair action complete', {
      projectKey: traceContext.projectKey,
      signatureKey: toTraceKey(traceContext.signature),
      kind,
      success: result.success,
      addedCount: result.addedCount,
      updatedCount: result.updatedCount,
      existingCount: result.existingCount,
      failedCount: result.failedCount,
      conflictingCount: result.conflictingCount,
      requestedCount: result.requestedCount,
      queued: result.queued,
      elapsedMs: Date.now() - startedAt,
      error: result.error ?? null,
      failedParameters: (result.failedParameters ?? []).slice(0, 10),
      conflictingParameters: (result.conflictingParameters ?? []).slice(0, 10),
      failureMessages: (result.failureMessages ?? []).slice(0, 10),
    });
    if (!clean || (result.bindingConflictExistingCount || 0) > 0) {
      warnMappingFlow('mapping readiness repair action failed', {
        projectKey: traceContext.projectKey,
        signatureKey: toTraceKey(traceContext.signature),
        kind,
        success: result.success,
        error: result.error ?? null,
        failedCount: result.failedCount ?? 0,
        conflictingCount: result.conflictingCount ?? 0,
        bindingConflictExistingCount: result.bindingConflictExistingCount ?? 0,
        failedParameters: (result.failedParameters ?? []).slice(0, 10),
        conflictingParameters: (result.conflictingParameters ?? []).slice(0, 10),
        failureMessages: (result.failureMessages ?? []).slice(0, 10),
      });
    }
    (clean || !totallyFailed
      ? progressCallbacks.onStepComplete
      : progressCallbacks.onStepError)?.(progressStepId);
    return !totallyFailed;
  };

  if (effectiveRepairKinds.has('connector') &&
      !(await runRepairAction('connector', actions.addParameters))) {
    return finish(true);
  }

  const runsClassificationQuickFill =
    effectiveRepairKinds.has('additional') ||
    effectiveRepairKinds.has('bolt') ||
    (effectiveRepairKinds.has('connector') &&
      Boolean(actions.resolveFamilyRepairKindsAfterClassification));
  if (runsClassificationQuickFill) {
    const startedAt = Date.now();
    progressCallbacks.onStepStart?.('quickFillClassification');
    warnMappingFlow('mapping readiness quick fill classification start', {
      projectKey: traceContext.projectKey,
      signatureKey: toTraceKey(traceContext.signature),
      repairKinds,
    });
    try {
      const result = await actions.runQuickFillAutoFillRepair('classification');
      warnMappingFlow('mapping readiness quick fill classification complete', {
        projectKey: traceContext.projectKey,
        signatureKey: toTraceKey(traceContext.signature),
        success: result.success,
        attempted: result.attempted,
        filledCount: result.filledCount,
        savedCount: result.savedCount,
        failureCount: result.failureCount,
        elapsedMs: Date.now() - startedAt,
        error: result.error ?? null,
      });
      progressCallbacks.onStepComplete?.('quickFillClassification');
    } catch (error) {
      progressCallbacks.onStepError?.('quickFillClassification');
      warnMappingFlow('mapping readiness quick fill classification failed', {
        projectKey: traceContext.projectKey,
        signatureKey: toTraceKey(traceContext.signature),
        elapsedMs: Date.now() - startedAt,
        error: error instanceof Error ? error.message : 'Quick Fill classification failed.',
      });
    } finally {
      invalidateFamilySnapshotCache(traceContext.projectKey);
    }

    if (actions.resolveFamilyRepairKindsAfterClassification) {
      const refreshedRepairKinds = await actions.resolveFamilyRepairKindsAfterClassification();
      latestValidatedFamilyRepairKinds = new Set(refreshedRepairKinds);
      refreshedRepairKinds.forEach((kind) => effectiveRepairKinds.add(kind));
      warnMappingFlow('mapping readiness family repair plan refreshed', {
        projectKey: traceContext.projectKey,
        signatureKey: toTraceKey(traceContext.signature),
        originalRepairKinds: repairKinds,
        refreshedRepairKinds,
        effectiveRepairKinds: Array.from(effectiveRepairKinds),
      });
    }
  }

  if (effectiveRepairKinds.has('bolt') &&
      !(await runRepairAction('bolt', actions.addBoltParameters))) {
    return finish(true);
  }
  ranFamilyMutationAction = effectiveRepairKinds.has('bolt');
  if (effectiveRepairKinds.has('additional') &&
      !(await runRepairAction('additional', actions.addAdditionalCategoryParameters))) {
    return finish(true);
  }
  ranFamilyMutationAction = ranFamilyMutationAction || effectiveRepairKinds.has('additional');

  if (actions.resolveFamilyRepairKindsAfterClassification && ranFamilyMutationAction) {
    const refreshedRepairKinds = await actions.resolveFamilyRepairKindsAfterClassification();
    latestValidatedFamilyRepairKinds = new Set(refreshedRepairKinds);
    warnMappingFlow('mapping readiness family repair finalization validated', {
      projectKey: traceContext.projectKey,
      signatureKey: toTraceKey(traceContext.signature),
      refreshedRepairKinds,
    });
  }
  const connectorFinalizationRequired = !actions.resolveFamilyRepairKindsAfterClassification ||
    latestValidatedFamilyRepairKinds.has('connector');
  if (effectiveRepairKinds.has('connector') && runsClassificationQuickFill &&
      connectorFinalizationRequired &&
      !(await runRepairAction(
        'connector',
        (repairOptions) => actions.addParameters({
          ...repairOptions,
          familyTargetMode: 'newSincePreviousConnectorSetup',
        }),
        'connectorFinalization'
      ))) {
    return finish(true);
  }
  if (effectiveRepairKinds.has('processProject') &&
      !(await runRepairAction('processProject', actions.addProcessAndProjectParameters))) {
    return finish(true);
  }
  if (effectiveRepairKinds.has('mapping') &&
      !(await runRepairAction(
        'mapping',
        (repairOptions) => actions.addMappedParameters(mappedParameterNames, repairOptions)
      ))) {
    return finish(true);
  }

  const runsFullQuickFill = runsClassificationQuickFill || effectiveRepairKinds.has('mapping');
  if (!runsFullQuickFill) {
    return finish();
  }
  const startedAt = Date.now();
  progressCallbacks.onStepStart?.('quickFill');
  warnMappingFlow('mapping readiness quick fill repair start', {
    projectKey: traceContext.projectKey,
    signatureKey: toTraceKey(traceContext.signature),
    repairKinds,
  });
  try {
    quickFillResult = await actions.runQuickFillAutoFillRepair('full');
  } catch (error) {
    progressCallbacks.onStepError?.('quickFill');
    warnMappingFlow('mapping readiness quick fill repair failed', {
      projectKey: traceContext.projectKey,
      signatureKey: toTraceKey(traceContext.signature),
      elapsedMs: Date.now() - startedAt,
      errorType: error instanceof Error ? error.name : typeof error,
      message: error instanceof Error ? error.message : 'Quick Fill repair failed.',
    });
    throw error;
  }
  warnMappingFlow('mapping readiness quick fill repair complete', {
    projectKey: traceContext.projectKey,
    signatureKey: toTraceKey(traceContext.signature),
    success: quickFillResult.success,
    attempted: quickFillResult.attempted,
    filledCount: quickFillResult.filledCount,
    savedCount: quickFillResult.savedCount,
    failureCount: quickFillResult.failureCount,
    totalRows: quickFillResult.totalRows,
    updateCount: quickFillResult.updateCount,
    elapsedMs: Date.now() - startedAt,
    error: quickFillResult.error ?? null,
  });
  const totallyFailed = quickFillResult.failureCount > 0 && quickFillResult.savedCount === 0;
  (totallyFailed
    ? progressCallbacks.onStepError
    : progressCallbacks.onStepComplete)?.('quickFill');
  invalidateFamilySnapshotCache(traceContext.projectKey);
  return finish(totallyFailed);
}

function buildRepairActionFallbackMessage(
  kind: MappingParameterRepairKind,
  result: ParameterAdditionResult
): string {
  const label = getRepairKindLabel(kind);
  const failedCount = result.failedCount ?? 0;
  const conflictingCount = result.conflictingCount ?? 0;
  const parameters = [
    ...(result.failedParameters ?? []),
    ...(result.conflictingParameters ?? []),
  ].slice(0, 6);
  const suffix = parameters.length > 0 ? ` Affected parameters: ${parameters.join(', ')}.` : '';
  if (failedCount > 0 || conflictingCount > 0) {
    const parts = [
      failedCount > 0 ? `${failedCount} failed` : null,
      conflictingCount > 0 ? `${conflictingCount} conflicted` : null,
    ].filter(Boolean);
    return `${label} setup did not complete because Revit returned ${parts.join(' and ')} parameter operation${failedCount + conflictingCount === 1 ? '' : 's'}.${suffix} Check the selected setup page and click Retry setup.`;
  }
  if (kind === 'bolt') {
    return 'Bolt-property setup did not complete. The selected target families may still be missing or not editable, or the selected bolt properties may be missing from the shared parameter file. Check the Bolts page, then click Retry setup.';
  }
  if (kind === 'additional') {
    return 'Additional-category setup did not complete. Matching configured object-type families may still be missing or not editable, or the selected parameters may be missing from the shared parameter file. Check the Additional categories page, then select Retry setup.';
  }
  if (kind === 'connector') {
    return 'Connector setup did not complete. Check the connector parameter selections and shared parameter file, then click Retry setup.';
  }
  return `${label} setup did not complete. Check the selected setup page and click Retry setup.`;
}

export function getRepairFailureMessage(results: MappingRepairActionResult[]): string | null {
  const failed = results.find(({ result }) => !result.success);
  if (failed) {
    return failed.result.error || failed.result.failureMessages?.[0] ||
      buildRepairActionFallbackMessage(failed.kind, failed.result);
  }
  const partial = results.find(({ result }) =>
    (result.failedCount || 0) > 0 || (result.conflictingCount || 0) > 0
  );
  return partial
    ? partial.result.error || partial.result.failureMessages?.[0] ||
      buildRepairActionFallbackMessage(partial.kind, partial.result)
    : null;
}

export function resolvePostVerificationFailureMessage(
  _rawFailureMessage: string | null,
  quickFillFailureMessage: string | null
): string | null {
  return quickFillFailureMessage;
}

export function mapBoltRepairResultToParameterAdditionResult(
  result: BoltConfigurationResult
): ParameterAdditionResult {
  const parameterCount = result.details?.parameterCount ?? 0;
  return {
    success: result.success,
    addedCount: result.success && result.action !== 'no_action' ? parameterCount : 0,
    existingCount: result.success && result.action === 'no_action' ? parameterCount : 0,
    failedCount: result.success ? 0 : 1,
    requestedCount: parameterCount,
    resolvedFamilyTargetCount: result.details?.resolvedFamilyTargetCount,
    modifiedFamilyCount: result.details?.modifiedFamiliesCount,
    undoAvailable: result.undoAvailable,
    error: result.success ? undefined : result.error || result.message,
    failureMessages: result.success ? [] : [result.error || result.message],
  };
}
